import { isString } from '@app/util';
import * as R from 'ramda';

/**
 * Entity types.
 */
export enum EntityType {
  Array = 'array',
  Object = 'object',
  Value = 'value'
}

/**
 * Property names map that is used to map control name to property name.
 */
export interface PropertyNamesMap {
  [key: string]: string | PropertyNameMapper;
}

/**
 * Property names mapper options.
 *
 * @field {string} entityName - domain entity name to which the mapper is referring to.
 * @field {EntityType} entityType - the type of the domain entity.
 */
export interface PropertyNamesMapperOpts {
  entityName?: string;
  entityType?: EntityType;
  parentEntityName?: string;
}

/**
 * This class should be used when we want to map property name of form control to domain property name.
 * ex. ACL permissions or Valdr validation schema.
 */
export class PropertyNameMapper {

  constructor(private propertyNamesMap: PropertyNamesMap, private opts: PropertyNamesMapperOpts = {}) {
    if (!this.propertyNamesMap) {
      throw Error('You must provide PropertyNamesMap in PropertyNameMapper\'s constructor');
    }
  }

  getPropertyName(name: string): string {
    const nameOrMapper = this.propertyNamesMap[name];

    if (isString(nameOrMapper)) {
      return nameOrMapper;
    }

    return name;
  }

  getPropertyNameMapper(name: string): PropertyNameMapper {
    const nameOrMapper = this.propertyNamesMap[name];

    if (!nameOrMapper) {
      return null;
    } else if (R.is(PropertyNameMapper, nameOrMapper)) {
      return nameOrMapper as PropertyNameMapper;
    }

    return null;
  }

  getPropertyNameMap(): PropertyNamesMap {
    return this.propertyNamesMap;
  }

  getEntityName() {
    return this.opts.entityName;
  }

  getParentEntityName() {
    return this.opts.parentEntityName;
  }

  getEntityType() {
    return this.opts.entityType;
  }

  isArrayEntity() {
    return this.getEntityType() === EntityType.Array;
  }

  isObjectEntity() {
    return this.getEntityType() === EntityType.Object;
  }

  isValue() {
    return this.getEntityType() === EntityType.Value;
  }

}
